import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject, Subject, Subscription } from "rxjs";
import { distinctUntilChanged, filter, finalize, map, switchMap, take, tap } from "rxjs/operators";
import { Contract } from "src/app/lib/contract/classes";
import { IContractsProviderService } from "src/app/lib/contract/interfaces";
import { ContractService } from "src/app/lib/contract/services/contract.service";
import { ErrorWrapper } from "src/app/lib/error/classes";
import { ErrorDialogComponent } from "src/app/lib/error/components/dialog/error-dialog/error-dialog.component";
import { Issue } from "src/app/lib/issue/classes";
import { IssueService } from "src/app/lib/issue/services/issue.service";
import { Address } from "src/app/lib/location/classes";
import { LocationService } from "src/app/lib/location/services/location.service";
import { Offer } from "src/app/lib/offer/classes";
import { IssueTypes } from "src/app/lib/shared/globals";
import { IConcurrencyProviderService, IIssuesProvider, IOffersProviderService } from "src/app/lib/shared/interfaces";
import { Contact, Customer } from "../../../classes";
import { IContactsProviderService } from "../../../interfaces";
import { CustomerService } from "../../../services/customer.service";

@Injectable()
export class CustomerDetailViewComponentService implements IContactsProviderService, IContractsProviderService, IIssuesProvider, IOffersProviderService, IConcurrencyProviderService {

    private _customerId = new ReplaySubject(1);
    customerId$ = this._customerId.asObservable();
    entityIdentifier$ = this.customerId$;

    private _customer = new ReplaySubject(1);
    customer$ = this._customer.asObservable();
    concurrencyStamp$ = this._customer.pipe(map((customer: Customer) => customer.concurrencystamp));

    private _loading = new Subject<boolean>();
    loading$ = this._loading.asObservable();

    private _checkingVersion = new BehaviorSubject(false);
    checkingVersion$ = this._checkingVersion.pipe(tap(() => this._changeDetectorRef.detectChanges()));

    _issues = new BehaviorSubject([]);
    issues$ = this._issues.asObservable();

    private _visibleIssueTypes = new BehaviorSubject<{ 'issues': boolean, 'incidents': boolean, 'changes': boolean, 'projects': boolean }>({ 'changes': true, 'incidents': true, 'issues': true, 'projects': true });
    visibleIssueTypes$ = this._visibleIssueTypes.asObservable();
    visibleIssues$ = combineLatest([this.visibleIssueTypes$, this.issues$]).pipe(map(([visibleTypes, issues]) => {
        return issues.filter((x: Issue) => {
            switch (x.issuetype) {
                case IssueTypes.Change:
                    return visibleTypes.changes;
                case IssueTypes.Incident:
                    return visibleTypes.incidents;
                case IssueTypes.Issue:
                    return visibleTypes.issues;
                case IssueTypes.Project:
                    return visibleTypes.projects;
            }
            return false;
        });
    }));

    issueCount$ = this.issues$.pipe(map((x: Issue[]) => x.filter(x => x.issuetype === IssueTypes.Issue).length));
    incidentCount$ = this.issues$.pipe(map((x: Issue[]) => x.filter(x => x.issuetype === IssueTypes.Incident).length));
    projectCount$ = this.issues$.pipe(map((x: Issue[]) => x.filter(x => x.issuetype === IssueTypes.Project).length));
    changeCount$ = this.issues$.pipe(map((x: Issue[]) => x.filter(x => x.issuetype === IssueTypes.Change).length));

    private _contacts = new ReplaySubject<Contact[]>(1);
    contacts$ = this._contacts.asObservable();

    private _contracts = new ReplaySubject<Contract[]>(1);
    contracts$ = this._contracts.asObservable();

    private _offers = new ReplaySubject<Offer[]>(1);
    offers$ = this._offers.asObservable();

    private _currentCustomerSub: Subscription = null;
    private _subscriptions: Subscription[] = [];

    validStamp$: Observable<boolean> = this._customer.pipe(switchMap((x: Customer) => {
        this._checkingVersion.next(true);
        return this._customerService.checkCustomerVersion(x).pipe(finalize(() => this._checkingVersion.next(false)));
    }));

    constructor(
        private _route: ActivatedRoute,
        private _customerService: CustomerService,
        private _contractService: ContractService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _issueService: IssueService,
        private _locationService: LocationService,
        private _router: Router,
        private _dialog: MatDialog
    ) {
        this.setUp();
    }

    deleteCurrentCustomer(request): Observable<Object> {
        let subject = new Subject();
        this.customerId$
            .pipe(take(1), switchMap((customerId: number) => this._customerService.deleteCustomer(customerId, request)))
            .subscribe({
                next: () => {
                    this._router.navigate(['/home']);
                    subject.next();
                },
                error: (error: HttpErrorResponse) => subject.error(error),
                complete: () => subject.complete()
            });
        return subject.asObservable();
    }

    dispose(): void {
        for (let sub of this._subscriptions) sub.unsubscribe();
        this._subscriptions = [];
        this._unsubscribeCurrentCustomer();
    }

    refreshCurrentCustomer(): Observable<Customer> {
        let subject = new Subject<Customer>();
        this._customerId.pipe(
            take(1),
            switchMap((x: number) => this._customerService.getCustomerById(x, false))
        ).subscribe(subject);
        return subject.asObservable();
    }

    refreshCurrentCustomerContacts(): Observable<Contact[]> {
        let subject = new Subject<Contact[]>();
        this._customerId.pipe(
            take(1),
            switchMap((x: number) => this._customerService.getCustomerContacts(x)),
            tap((contacts: Contact[]) => this._contacts.next(contacts))
        ).subscribe(subject);
        return subject.asObservable();
    }

    refreshCurrentCustomerContracts(): Observable<Contract[]> {
        let subject = new Subject<Contract[]>();
        this._customerId.pipe(
            take(1),
            switchMap((x: number) => this._contractService.getCustomerContracts(x, false))
        ).subscribe(subject);
        return subject.asObservable();
    }

    refreshCurrentCustomerOffers(): Observable<Offer[]> {
        let subject = new Subject<Offer[]>();
        this._customerId.pipe(
            take(1),
            switchMap((x: number) => this._customerService.getCustomerOffers(x)),
            tap(offers => this._offers.next(offers))
        ).subscribe(subject);
        return subject.asObservable();
    }

    saveChanges(customer: Customer = null, note: string = null, address: Address = null): Observable<Customer> {
        
        return (address? this._locationService[(typeof address.addressid === 'number' && address.addressid !== 0)? 'updateAddress': 'createAddress'](address): of(address))
            .pipe(switchMap((address: Address) => {
                customer.customeraddress = address.addressid;
                return this._customerService.updateCustomer(customer, note)
            }));
    }

    setCustomerId(customerId: number) {
        this._loading.next(true);
        this._unsubscribeCurrentCustomer();
        this._currentCustomerSub = combineLatest([this._customerService.getCustomerById(customerId, false), this._issueService.getCustomerIssues(customerId), this._contractService.getCustomerContracts(customerId, true), this._customerService.getCustomerOffers(customerId), this._customerService.getCustomerContacts(customerId)]).subscribe({
            next: ([customer, issues, contracts, offers, contacts]) => {
                this._customer.next(customer);
                this._contracts.next(contracts);
                this._contacts.next(contacts);
                this._offers.next(offers);
                this._issues.next(issues);
                this._loading.next(false);
            },
            error: (error: HttpErrorResponse) => {
                switch(error.status){
                    case 404:
                        this._dialog.open(ErrorDialogComponent, {
                            panelClass: 'no-padding-dialog-container',
                            disableClose: true,
                            data: {
                                'description': `Es konnte kein Kunde mit der ID ${customerId} gefunden werden. Ist der Link eventuell veraltet oder wurde der Kunde in der Zwischenzeit gelöscht?`,
                                'error': error,
                                'showNavigateToMainPage': true
                            } as ErrorWrapper
                        });
                        break;
                }
                this._loading.next(false);
            }
        });
    }

    setVisibleIssueTypes = (types: { 'issues': boolean, 'incidents': boolean, 'changes': boolean, 'projects': boolean }) => this._visibleIssueTypes.next(types);

    setUp(): void {
        this._subscriptions.push(...[
            this._route.params.pipe(map(params => parseInt(params['id'])), filter(x => typeof (x) === 'number')).subscribe((customerId: number) => {
                this._customerId.next(customerId);
            }),
            this.customerId$.pipe(distinctUntilChanged()).subscribe((identifier: number) => this.setCustomerId(identifier))
        ]);
    }

    private _unsubscribeCurrentCustomer() {
        if (this._currentCustomerSub) {
            this._currentCustomerSub.unsubscribe();
            this._currentCustomerSub = null;
        }
    }

}